import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Controller,
  useWatch,
  type Control,
  type UseFormReturn,
} from "react-hook-form";
import { PREFECTURES } from "@/utils/prefecture";
import { useCityWithExternalState } from "@/hooks/useCity";
import { MIN_TAP_TARGET } from "@/styles/responsive";
import { REAL_ESTATE_KINDS, type V2SearchChoices } from "../../api/v2Search";
import {
  SECURITY_DETAIL_KEYS,
  cityValue,
  defaultV2SearchConditions,
  type V2SearchConditions,
} from "../../utils/v2SearchParams";
import {
  ChoiceField,
  chipTags,
  CheckField,
  DateRangeField,
  NumberRangeField,
  SelectField,
  TextConditionField,
  ToggleChoiceField,
  TriStateField,
  toOptions,
  type ChoiceOption,
} from "./V2SearchFields";

type SearchControl = Control<V2SearchConditions>;

/** 検索条件と結果のバッジで同じ表記を使う。 */
const judgementLabels: Record<string, string> = {
  判定不能: "判定不能",
};
const judgementOptions = (values: readonly string[]): ChoiceOption[] =>
  values.map((value) => ({ value, label: judgementLabels[value] ?? value }));

/** Keys shown above the fold; everything else lives under 詳細な条件. */
const BASIC_KEYS = new Set<keyof V2SearchConditions>([
  "kinds",
  "prefectures",
  "cities",
  "location",
  "locationHistory",
  "number",
  "realEstateNumber",
  "buildingName",
  "ownerName",
  "ownerAddress",
  "ownerHistory",
  "ownerCorporation",
  "holderName",
  "debtorName",
  "securityHistory",
  "landAreaMin",
  "landAreaMax",
  "floorAreaMin",
  "floorAreaMax",
  "builtFrom",
  "builtTo",
]);

/** 詳細な条件のうち、指定されている数（折りたたみの見出しに出す） */
export const countDetailConditions = (values: V2SearchConditions): number =>
  (Object.keys(values) as Array<keyof V2SearchConditions>).filter((key) => {
    if (BASIC_KEYS.has(key)) return false;
    const value = values[key];
    return Array.isArray(value)
      ? value.length > 0
      : typeof value === "string"
      ? value.trim() !== ""
      : value;
  }).length;

const prefectureOf = (cityCode: string): number => Number(cityCode.slice(0, 2));

const prefectureName = (id: string): string =>
  PREFECTURES.find((pref) => String(pref.id) === id)?.name ?? id;

/** 都道府県（複数）と、選んだ都道府県の市区町村（複数） */
const RegionField = ({
  control,
  setValue,
}: {
  control: SearchControl;
  setValue: UseFormReturn<V2SearchConditions>["setValue"];
}): JSX.Element => {
  const prefectures = useWatch({ control, name: "prefectures" });
  const cities = useWatch({ control, name: "cities" });
  useEffect(() => {
    // Drop cities whose prefecture was deselected.
    const kept = cities.filter((entry) =>
      prefectures.includes(entry.split(":")[0])
    );
    if (kept.length !== cities.length) setValue("cities", kept);
  }, [prefectures, cities, setValue]);
  // Same set of prefectures, same key: keeps the city request stable across renders.
  const prefectureKey = [...prefectures]
    .sort((a, b) => Number(a) - Number(b))
    .join(",");
  const prefCodes = useMemo(
    () => (prefectureKey === "" ? [] : prefectureKey.split(",")),
    [prefectureKey]
  );
  const { cityList, isCityListReady, hasCityListError } =
    useCityWithExternalState(prefCodes);
  // Remember names of every loaded city so selected chips keep their labels while a new list loads.
  const [names, setNames] = useState(() => new Map<string, string>());
  const cityName = (value: string): string => names.get(value) ?? "…";
  const options = useMemo(
    () =>
      (cityList?.list ?? []).map((city) => ({
        value: cityValue(prefectureOf(city.cityCode), city.id),
        label: city.name,
      })),
    [cityList]
  );
  useEffect(() => {
    if (options.length === 0) return;
    setNames((previous) => {
      const next = new Map(previous);
      options.forEach((option) => next.set(option.value, option.label));
      return next;
    });
  }, [options]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="prefectures"
          render={({ field }) => (
            <Box>
              <Typography
                component="label"
                htmlFor="v2-search-prefectures"
                variant="body2"
                fontWeight="bold"
                sx={{ display: "block", mb: 0.5 }}
              >
                都道府県
              </Typography>
              <Autocomplete<string, true>
                id="v2-search-prefectures"
                multiple
                size="small"
                options={PREFECTURES.map((pref) => String(pref.id))}
                getOptionLabel={prefectureName}
                value={field.value}
                onChange={(_, value) => {
                  field.onChange(value);
                }}
                renderTags={chipTags(prefectureName)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={field.value.length ? undefined : "選択"}
                  />
                )}
              />
            </Box>
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          name="cities"
          render={({ field }) => {
            const { value } = field;
            return (
              <Box>
                <Typography
                  component="label"
                  htmlFor="v2-search-cities"
                  variant="body2"
                  fontWeight="bold"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  市区町村
                </Typography>
                <Autocomplete<string, true>
                  id="v2-search-cities"
                  multiple
                  size="small"
                  disabled={prefectures.length === 0}
                  loading={prefectures.length > 0 && !isCityListReady}
                  loadingText="読み込み中"
                  noOptionsText={
                    hasCityListError
                      ? "市区町村を取得できませんでした"
                      : "該当なし"
                  }
                  options={options.map((option) => option.value)}
                  getOptionLabel={cityName}
                  value={value}
                  onChange={(_, next) => {
                    field.onChange(next);
                  }}
                  renderTags={chipTags(cityName)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        prefectures.length === 0
                          ? "都道府県を選ぶと選べます"
                          : value.length
                          ? undefined
                          : "指定なしは都道府県全体"
                      }
                    />
                  )}
                />
              </Box>
            );
          }}
        />
      </Grid>
    </Grid>
  );
};

const SectionHeading = ({ children }: { children: string }): JSX.Element => (
  <Typography
    component="h3"
    variant="subtitle2"
    color="primary"
    fontWeight="bold"
    sx={{ mt: 1 }}
  >
    {children}
  </Typography>
);

const Field = ({
  children,
  wide = false,
}: {
  children: React.ReactNode;
  wide?: boolean;
}): JSX.Element => (
  <Grid item xs={12} sm={wide ? 12 : 6} md={wide ? 8 : 4}>
    {children}
  </Grid>
);

const DetailConditions = ({
  control,
  choices,
}: {
  control: SearchControl;
  choices: V2SearchChoices;
}): JSX.Element => {
  const securityExists = useWatch({ control, name: "securityExists" });
  const noSecurity = securityExists === "no";
  return (
    <Stack spacing={2}>
      <SectionHeading>物件（表題部）</SectionHeading>
      <Grid container spacing={2}>
        <Field>
          <ChoiceField
            control={control}
            name="landCategories"
            label="地目"
            options={toOptions(choices.landCategories)}
            freeSolo
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="landAreaSources"
            label="地積の出所"
            options={toOptions(choices.landAreaSources)}
            helperText="旧単位換算は尺貫法の地積を㎡に換算した値"
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="buildingTypes"
            label="種類（建物）"
            options={toOptions(choices.buildingTypes)}
            freeSolo
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="structures"
            label="構造"
            options={choices.structureCategories}
          />
        </Field>
        <Field>
          <NumberRangeField
            control={control}
            minName="ageMin"
            maxName="ageMax"
            label="築年数"
            unit="年"
            helperText="今日の日付から新築年月日の範囲に直して検索します"
          />
        </Field>
        <Field>
          <NumberRangeField
            control={control}
            minName="floorsMin"
            maxName="floorsMax"
            label="地上階数"
            unit="階"
            helperText="区分建物は一棟の地上階数"
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="builtStatuses"
            label="新築年月日の状態"
            options={toOptions(choices.newConstructionDateStatuses)}
          />
        </Field>
        <Field>
          <SelectField
            control={control}
            name="closed"
            label="閉鎖した登記"
            emptyLabel="含める"
            options={[
              { value: "exclude", label: "除く" },
              { value: "only", label: "閉鎖のみ" },
            ]}
          />
        </Field>
        <Field>
          <NumberRangeField
            control={control}
            minName="oneBuildingFloorAreaMin"
            maxName="oneBuildingFloorAreaMax"
            label="一棟の延床面積"
            unit="㎡"
          />
        </Field>
        <Field>
          <NumberRangeField
            control={control}
            minName="unitFloorMin"
            maxName="unitFloorMax"
            label="所在階（区分建物）"
            unit="階"
            helperText="地下はマイナス（地下1階 = -1）"
          />
        </Field>
        <Field>
          <TriStateField control={control} name="hasAnnex" label="附属建物" />
        </Field>
        <Field>
          <NumberRangeField
            control={control}
            minName="annexFloorAreaMin"
            maxName="annexFloorAreaMax"
            label="附属を含む床面積"
            unit="㎡"
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="titleOwnerName"
            label="表題部所有者の名称"
            placeholder="部分一致"
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="titleOwnerAddress"
            label="表題部所有者の住所"
            placeholder="部分一致"
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="siteLocation"
            label="敷地の所在及び地番（区分建物）"
            placeholder="部分一致"
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="siteRightKinds"
            label="敷地権の種類"
            options={toOptions(choices.siteRightKinds)}
          />
        </Field>
      </Grid>

      <Divider />
      <SectionHeading>所有者・甲区</SectionHeading>
      <Grid container spacing={2}>
        <Field>
          <TextConditionField
            control={control}
            name="ownerNumber"
            label="法人番号・会社法人等番号"
            placeholder="13桁または12桁"
          />
        </Field>
        <Field>
          <NumberRangeField
            control={control}
            minName="ownerCountMin"
            maxName="ownerCountMax"
            label="現在の所有者の人数"
            unit="人"
            helperText="単独は 1〜1、共有は 2〜"
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="ownerCautions"
            label="最終所有者の注意事項"
            options={toOptions(choices.ownerCautions)}
            freeSolo
          />
        </Field>
        <Field>
          <TriStateField
            control={control}
            name="ownerExternalCharacter"
            label="所有者の名称・住所の外字"
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="restrictions"
            label="現在の処分制限"
            options={toOptions(choices.dispositionRestrictions)}
          />
          <CheckField
            control={control}
            name="restrictionsHistory"
            label="抹消された処分制限も含める"
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="kokuPurposeKinds"
            label="甲区の登記の目的（権利）"
            options={toOptions(choices.kokuPurposeRightKinds)}
            freeSolo
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="kokuPurposeActions"
            label="甲区の登記の目的（動作）"
            options={toOptions(choices.kokuPurposeActions)}
            freeSolo
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="kokuReasons"
            label="甲区の原因"
            options={toOptions(choices.kokuReasonWords)}
            freeSolo
          />
        </Field>
        <Field>
          <DateRangeField
            control={control}
            fromName="kokuAcceptFrom"
            toName="kokuAcceptTo"
            label="甲区の受付日"
            helperText="受付日のない登記（職権・移記）は対象外です"
          />
        </Field>
        <Field>
          <DateRangeField
            control={control}
            fromName="kokuReasonDateFrom"
            toName="kokuReasonDateTo"
            label="甲区の原因日付"
          />
        </Field>
        <Field>
          <TriStateField
            control={control}
            name="kokuProvisional"
            label="甲区の仮登記"
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="trustPartyName"
            label="信託の当事者（委託者・受託者・受益者）"
            placeholder="部分一致"
          />
        </Field>
      </Grid>

      <Divider />
      <SectionHeading>担保権・乙区</SectionHeading>
      <Grid container spacing={2}>
        <Field>
          <TriStateField
            control={control}
            name="securityExists"
            label="現在有効な担保権"
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="securityKinds"
            label="担保権の種別"
            options={toOptions(choices.securityRightKinds)}
            freeSolo
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="debtorAddress"
            label="債務者の住所"
            placeholder="部分一致"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <DateRangeField
            control={control}
            fromName="securityAcceptFrom"
            toName="securityAcceptTo"
            label="担保権の設定の受付日"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <NumberRangeField
            control={control}
            minName="amountMin"
            maxName="amountMax"
            label="現在の債権額・極度額"
            unit="万円"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="otsukuReasons"
            label="乙区の原因"
            options={toOptions(choices.otsukuReasonWords)}
            freeSolo
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <NumberRangeField
            control={control}
            minName="rankMin"
            maxName="rankMax"
            label="実効順位"
            unit="番"
            helperText="1番のみは 1〜1"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="handlingBranch"
            label="取扱店"
            placeholder="部分一致"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <TriStateField
            control={control}
            name="securityProvisional"
            label="仮登記の担保権"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <TriStateField
            control={control}
            name="principalFixed"
            label="元本確定"
            yesLabel="確定"
            noLabel="未確定"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <TriStateField
            control={control}
            name="hasChanges"
            label="変遷"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <TriStateField
            control={control}
            name="jointUndetermined"
            label="共同担保物件未確定"
            yesLabel="未確定あり"
            noLabel="なし"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <TriStateField
            control={control}
            name="hasJointSecurity"
            label="共同担保"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="jointCatalogNumber"
            label="共同担保目録の記号・番号"
            placeholder="例 (あ)第1234号"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="jointItemLocation"
            label="共同担保の物件の所在"
            placeholder="部分一致"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="jointItemNumber"
            label="共同担保の物件の地番・家屋番号"
            placeholder="前方一致"
            disabled={noSecurity}
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="usageRightHolderName"
            label="用益権者（地上権・賃借権・地役権）"
            placeholder="部分一致"
          />
        </Field>
      </Grid>

      <Divider />
      <SectionHeading>取得・依頼・確度</SectionHeading>
      <Grid container spacing={2}>
        <Field>
          <DateRangeField
            control={control}
            fromName="acquiredFrom"
            toName="acquiredTo"
            label="PDF の取得日"
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="requestName"
            label="解析依頼の識別名"
            placeholder="部分一致"
          />
        </Field>
        <Field>
          <TextConditionField
            control={control}
            name="requestId"
            label="解析依頼の ID"
          />
          <CheckField
            control={control}
            name="mineOnly"
            label="自分が依頼したものだけ"
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="ownerStatuses"
            label="最終所有者の確度"
            options={judgementOptions(choices.judgementStatuses)}
            helperText="指定しない場合はすべてが対象です"
          />
        </Field>
        <Field>
          <ChoiceField
            control={control}
            name="securityStatuses"
            label="担保権の確度"
            options={judgementOptions(choices.judgementStatuses)}
          />
        </Field>
      </Grid>
    </Stack>
  );
};

export const V2SearchForm = ({
  form,
  choices,
  onSearch,
  onClear,
}: {
  form: UseFormReturn<V2SearchConditions>;
  choices: V2SearchChoices;
  onSearch: (values: V2SearchConditions) => void;
  onClear: () => void;
}): JSX.Element => {
  const { control, handleSubmit, setValue, getValues } = form;
  const detailCount = countDetailConditions(form.watch());
  // Open the details when the URL already carries one of them (back/share).
  const [detailsOpen, setDetailsOpen] = useState(detailCount > 0);
  const securityExists = useWatch({ control, name: "securityExists" });
  const noSecurity = securityExists === "no";

  useEffect(() => {
    if (!noSecurity) return;
    // 「なし」のとき担保権の詳細は送らないので、入力も空にしておく
    SECURITY_DETAIL_KEYS.forEach((key) => {
      const current = getValues(key);
      const empty = defaultV2SearchConditions[key];
      if (JSON.stringify(current) !== JSON.stringify(empty))
        setValue(key, empty as never);
    });
  }, [noSecurity, getValues, setValue]);

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event) => {
        void handleSubmit(onSearch)(event);
      }}
      sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 2 }}
    >
      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <ToggleChoiceField
              control={control}
              name="kinds"
              label="種別"
              options={toOptions(REAL_ESTATE_KINDS)}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <RegionField control={control} setValue={setValue} />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextConditionField
              control={control}
              name="location"
              label="所在"
              placeholder="部分一致"
            />
            <CheckField
              control={control}
              name="locationHistory"
              label="過去の所在も含める"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextConditionField
              control={control}
              name="number"
              label="地番・家屋番号"
              placeholder="前方一致（2文字以上）"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextConditionField
              control={control}
              name="realEstateNumber"
              label="不動産番号"
              placeholder="13桁"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextConditionField
              control={control}
              name="buildingName"
              label="建物の名称"
              placeholder="部分一致（マンション名など）"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextConditionField
              control={control}
              name="ownerName"
              label="所有者の名称"
              placeholder="部分一致"
            />
            <CheckField
              control={control}
              name="ownerHistory"
              label="過去の所有者も含める"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextConditionField
              control={control}
              name="ownerAddress"
              label="所有者の住所"
              placeholder="部分一致"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SelectField
              control={control}
              name="ownerCorporation"
              label="所有者の法人・個人"
              options={[
                { value: "yes", label: "法人" },
                { value: "no", label: "法人以外" },
              ]}
              helperText="「法人以外」は法人と識別できなかった者"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextConditionField
              control={control}
              name="holderName"
              label="担保権者の名称"
              placeholder="部分一致"
              disabled={noSecurity}
            />
            <CheckField
              control={control}
              name="securityHistory"
              label="抹消された担保権も含める"
              disabled={noSecurity}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextConditionField
              control={control}
              name="debtorName"
              label="債務者の名称"
              placeholder="部分一致"
              disabled={noSecurity}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <NumberRangeField
              control={control}
              minName="landAreaMin"
              maxName="landAreaMax"
              label="地積"
              unit="㎡"
              helperText="旧単位の地積は㎡に換算した値で比べます"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <NumberRangeField
              control={control}
              minName="floorAreaMin"
              maxName="floorAreaMax"
              label="床面積"
              unit="㎡"
              helperText="区分建物は専有部分（内法）"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DateRangeField
              control={control}
              fromName="builtFrom"
              toName="builtTo"
              label="新築年月日"
              helperText="和暦も入力できます（平成12年、H12.4.1 など）"
            />
          </Grid>
        </Grid>

        <Box>
          <Button
            type="button"
            onClick={() => {
              setDetailsOpen((open) => !open);
            }}
            aria-expanded={detailsOpen}
            aria-controls="v2-search-details"
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: detailsOpen ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              />
            }
            sx={{ minHeight: MIN_TAP_TARGET }}
          >
            詳細な条件
            {detailCount > 0 ? `（${detailCount}件を指定中）` : ""}
          </Button>
          {!detailsOpen && (
            <Typography variant="body2" color="text.secondary">
              地目・種類・構造・階数・差押・登記の目的・原因・受付日・債権額・共同担保・取得日・確度など
            </Typography>
          )}
          <Collapse in={detailsOpen} id="v2-search-details">
            <Box sx={{ pt: 1 }}>
              <DetailConditions control={control} choices={choices} />
            </Box>
          </Collapse>
        </Box>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{
            position: { xs: "sticky", md: "static" },
            bottom: 0,
            // Sticky only inside the phone modal, where it needs an opaque background.
            bgcolor: { xs: "background.paper", md: "transparent" },
            py: 1,
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={onClear}
            sx={{ minHeight: MIN_TAP_TARGET }}
          >
            条件をクリア
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SearchIcon />}
            sx={{ minHeight: MIN_TAP_TARGET, px: 4 }}
          >
            検索する
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
